import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchRosterFlags, RosterFlagsFetcher, RosterFlagsResult } from "../api/rosterFlagsAPI";
import { captureError } from "../utils/sentry";
import { safeGetItem } from "@mediatechgroup/app-version";

export type RosterFlagsState = "idle" | "loading" | "ready" | "error";

export interface UseRosterFlags {
  state: RosterFlagsState;
  result: RosterFlagsResult | null;
  retry: () => void;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Sorted, de-duplicated real ids — so the same people in a new order is the same request. */
const normalize = (ids?: Array<string | null | undefined>) =>
  Array.from(new Set((ids || []).filter((id): id is string => typeof id === "string" && UUID_RE.test(id)))).sort();

/**
 * The flags for the people on one roster. Re-reads only when the SET of
 * people changes (not on re-render, re-sort or paging).
 *
 * `error` is a state of its own on purpose: a roster whose flags could not be
 * read must say so, never look like a roster with nothing to flag.
 *
 * `fetcher` is for a screen that is not signed in as staff (the kiosk): it
 * asks the same question with its own sign-in. Left out, the staff endpoint
 * is used.
 */
export function useRosterFlags(
  subjects: {
    athleteIds?: Array<string | null | undefined>;
    registrationIds?: Array<string | null | undefined>;
  },
  fetcher?: RosterFlagsFetcher
): UseRosterFlags {
  const athleteKey = normalize(subjects.athleteIds).join(",");
  const registrationKey = normalize(subjects.registrationIds).join(",");

  const [state, setState] = useState<RosterFlagsState>("idle");
  const [result, setResult] = useState<RosterFlagsResult | null>(null);
  const [attempt, setAttempt] = useState(0);
  const seq = useRef(0);
  // Read through a ref so a caller passing a new function each render does
  // not re-ask for the flags.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const current = ++seq.current;
    if (!athleteKey && !registrationKey) {
      setState("idle");
      setResult(null);
      return;
    }
    setState("loading");
    const wanted = {
      athleteIds: athleteKey ? athleteKey.split(",") : [],
      registrationIds: registrationKey ? registrationKey.split(",") : [],
    };
    const read = fetcherRef.current
      ? fetcherRef.current(wanted)
      : fetchRosterFlags(safeGetItem("authToken"), wanted);
    read
      .then((next) => {
        // Only the newest read may land (the roster changed while this was in flight).
        if (current !== seq.current) return;
        setResult(next);
        setState("ready");
      })
      .catch((error) => {
        if (current !== seq.current) return;
        captureError(error, { component: "useRosterFlags", operation: "fetchRosterFlags" });
        setResult(null);
        setState("error");
      });
  }, [athleteKey, registrationKey, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return useMemo(() => ({ state, result, retry }), [state, result, retry]);
}
